/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback } from 'react';
import { ConnectionHandler, commitLocalUpdate, useRelayEnvironment } from 'react-relay';
import { useIsBacklogRoute } from '@jira/software__board-settings-rapidboard-config-common/src/useIsBacklogRoute.tsx';
import { useExperienceStart } from '@jira/platform__jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@jira/platform__jira-experience-tracker/src/ui/experience-success/index.tsx';
import { useExperienceFail } from '@jira/platform__jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { MutationError } from '@jira/platform__utils-mutation-error/src/index.tsx';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useRouterActions } from '@atlassian/react-resource-router';
import RelayDataID from '@atlassian/relay-data-id';
import type { useBoardCreateMutationUseBoardCreateMutation$data as useBoardCreateMutationData } from './__generated__/useBoardCreateMutationUseBoardCreateMutation.graphql.ts';
import type { SubmitData } from './BoardCreateFormValues.types.tsx';
import { fireBoardCreateErrorAnalytics } from './fireBoardCreateErrorAnalytics.tsx';
import { BOARD_CREATE_PACKAGE_NAME } from './boardTypes.constants.tsx';
import { useBoardCreateMutationUseBoardCreateMutation } from './useBoardCreateMutation.tsx';
import { useBoardCreateFlag } from './useBoardCreateFlag.tsx';
import { transformInput, buildBoardUrl, redirectToBacklog } from './buildBoardUrl.tsx';

const NO_OF_BOARDS_TO_SHOW = 25;

const successTitle = defineMessage({
	id: 'software-board-create.controllers.use-board-create-controller.success-title',
	defaultMessage: 'Board successfully created',
	description: 'Displayed as the title when a board is successfully created.',
});

const errorTitle = defineMessage({
	id: 'software-board-create.controllers.use-board-create-controller.error-title',
	defaultMessage: 'We couldn’t create the board',
	description: 'Displayed as the title when a board is failed to created.',
});

const errorDescription = defineMessage({
	id: 'software-board-create.controllers.use-board-create-controller.error-description',
	defaultMessage: 'Refresh the page and try again.',
	description: 'Displayed as the description when a board is failed to created.',
});

export type SubmitPayload = SubmitData & {
	onSuccess?: (result: useBoardCreateMutationData) => void;
	onError?: (error: unknown) => void;
};

const EXPERIENCE = 'createBoard';

export const useAddBoardToProject = (): (({
	id,
	projectId,
}: {
	id: string | undefined;
	projectId: string;
}) => void) => {
	const environment = useRelayEnvironment();
	const cloudId = useCloudId();

	return useCallback(
		({ id, projectId }: { id: string | undefined; projectId: string }) => {
			commitLocalUpdate(environment, (store) => {
				try {
					if (!id) throw new Error('Failed to add board to project, missing id');
					if (!projectId) throw new Error('Failed to add board to project, missing projectId');

					const projectRelayDataId = RelayDataID(
						{
							id: JiraProjectAri.create({
								siteId: cloudId,
								projectId: String(projectId),
							}),
						},
						'JiraProject',
					);
					if (!projectRelayDataId)
						throw new Error(`Failed to create project relay data id, projectId: ${projectId}`);

					const project = store.get(projectRelayDataId);
					if (!project) throw new Error(`Failed to find the project, projectId: ${projectId}`);

					const projectBoardsConnection = project.getLinkedRecord('boards', {
						first: NO_OF_BOARDS_TO_SHOW,
					});
					if (!projectBoardsConnection)
						throw new Error('Failed to find the project-boards connection');

					const newNode = store.get(id);
					if (!newNode) throw new Error(`Failed to find the new board, id: ${id}`);

					const newEdge = ConnectionHandler.createEdge(
						store,
						projectBoardsConnection,
						newNode,
						'JiraBoardEdge',
					);
					ConnectionHandler.insertEdgeBefore(projectBoardsConnection, newEdge);
				} catch (error) {
					fireBoardCreateErrorAnalytics({
						id: 'addBoardToProject',
						error:
							error instanceof Error
								? error
								: new Error(`Failed to add board to project: ${error}`),
					});
				}
			});
		},
		[environment, cloudId],
	);
};

export const useBoardCreateController = (): {
	submit: ({
		boardType,
		locationProjectId,
		formValues,
		onSuccess,
		onError,
	}: SubmitPayload) => Promise<void>;
} => {
	const { handleMutation } = useBoardCreateMutationUseBoardCreateMutation();
	const { showSuccessFlag, showErrorFlag } = useBoardCreateFlag();
	const { push } = useRouterActions();
	const { formatMessage } = useIntl();
	const addBoardToProject = useAddBoardToProject();

	const isBacklogRoute = useIsBacklogRoute();
	const onExperienceStart = useExperienceStart({
		experience: EXPERIENCE,
		analyticsSource: BOARD_CREATE_PACKAGE_NAME,
	});
	const onExperienceSuccess = useExperienceSuccess({ experience: EXPERIENCE });
	const onExperienceFail = useExperienceFail({ experience: EXPERIENCE });

	const submit = useCallback(
		async ({
			boardType,
			locationProjectId,
			formValues,
			onSuccess,
			onError,
		}: SubmitPayload): Promise<void> => {
			const input = transformInput({ boardType, locationProjectId, formValues });
			try {
				onExperienceStart();
				const result = await handleMutation(input);

				if (!result?.jira?.createBoard?.success) {
					throw result?.jira?.createBoard?.errors?.length
						? new MutationError(result?.jira?.createBoard?.errors?.[0])
						: new Error('Failed to create board');
				}

				onSuccess?.(result);
				showSuccessFlag({
					title: formatMessage(successTitle),
				});
				addBoardToProject({
					projectId: locationProjectId,
					id: result.jira?.createBoard?.board?.__id,
				});
				onExperienceSuccess();

				const resultBoardId = result?.jira?.createBoard?.board?.boardId;
				const resultBoardType = result?.jira?.createBoard?.board?.boardType;
				const boardUrl = buildBoardUrl(result?.jira?.createBoard?.board?.boardUrl, resultBoardType);

				if (!boardUrl) {
					fireBoardCreateErrorAnalytics({
						error: new Error('Failed to generate board URL after board creation'),
						attributes: {
							boardId: resultBoardId,
							boardType: resultBoardType,
						},
						id: 'generateUrl',
					});
				} else {
					resultBoardType === 'SCRUM' && isBacklogRoute
						? redirectToBacklog(boardUrl)
						: push(boardUrl);
				}
			} catch (error) {
				let errMsg: string | undefined;
				if (error instanceof MutationError && error.message) {
					errMsg = error.message;
				}
				onError?.(errMsg || error);
				showErrorFlag({
					title: formatMessage(errorTitle),
					description: errMsg || formatMessage(errorDescription),
				});
				fireBoardCreateErrorAnalytics({
					error: new Error(`Failed to create board: ${errMsg || error}`, { cause: error }),
					id: 'createBoard',
				});
				onExperienceFail(
					BOARD_CREATE_PACKAGE_NAME,
					// oxlint-disable-next-line no-nested-ternary
					errMsg ? new Error(errMsg) : error instanceof Error ? error : new Error(String(error)),
				);
			}
		},
		[
			onExperienceStart,
			handleMutation,
			showSuccessFlag,
			formatMessage,
			addBoardToProject,
			onExperienceSuccess,
			isBacklogRoute,
			push,
			showErrorFlag,
			onExperienceFail,
		],
	);

	return { submit };
};
